import React, { CSSProperties, useEffect, useRef, useState } from "react"

import icClose from "@/assest/ic_close.png"
import icExit from "@/assest/ic_exit.png"
import icFile from "@/assest/ic_file.png"
import icPrivacy from "@/assest/ic_privacy.svg"
import icSetting from "@/assest/ic_setting.png"
import icVideoStatistic from "@/assest/ic_video_statistic.png"
import { selectIsChromiumBlinkBrowser } from "@/stores/app_slice"
import { selectConnStats } from "@/stores/conn_stats_slice"
import { useAppDispatch, useAppSelector } from "@/store"
import {
  selectIsFullScreen,
  selectIsOpenExitModal,
  setIsOpenExitModal,
} from "@/stores/remote_desk_state_slice"
import { ConnectionUseCase } from "@/usecases/connection"
import {
  VIRTUAL_MOUSE_MOVE_EVENT,
  VIRTUAL_MOUSE_UP_EVENT,
  VirtualMousePointerEventDetail,
} from "@/hooks/use_virtual_mouse"
import { SettingEntryPosCache } from "@/utils/storage"
import { clamp, mytest } from "@/utils/tools"
import { getOS, OS } from "@/utils/os"
import { AppDialog } from "@/widgets/AppDialog"
import { AppIconButton, AppIconTextButton } from "@/widgets/Common"
import { Box, Flex } from "@chakra-ui/react"
import { FileContent } from "./file_content"
import { exitDocumentFullScreen, FullScreenButton } from "./full_screen"
import { PrivacyContent } from "./privacy_content"
import { SettingsContent } from "./settings_content"
import { StatisticContent } from "./statistic_content"
import type { NetworkQualityStatus } from "@/usecases/network_quality"

interface Position {
  x: number
  y: number
}

const networkQualityColors: Record<NetworkQualityStatus, string> = {
  perfect: "#2196F3",
  good: "#4CAF50",
  normal: "#FFEB3B",
  bad: "#B71C1C",
}

const networkQualityShadows: Record<NetworkQualityStatus, string> = {
  perfect: "rgba(33, 150, 243, 0.49)",
  good: "rgba(76, 175, 80, 0.49)",
  normal: "rgba(255, 235, 59, 0.49)",
  bad: "rgba(183, 28, 28, 0.49)",
}

// 设置弹窗入口
export const SettingEntry = () => {
  // 悬浮球的大小
  const size = 46
  const visibleSize = size * 0.3
  const settingGap = 12
  const dragMoveThreshold = 5
  const windowPadding = 16
  // 是否正在拖拽悬浮球
  const [isDraggingBall, setIsDraggingBall] = useState(false)
  // 是否正在拖拽设置弹窗
  const [isDraggingSetting, setIsDraggingSetting] = useState(false)
  const [isChildModalOpen, setIsChildModalOpen] = useState(false)
  // 半隐藏悬浮球 hover 时，是否临时完整显示
  const [isBallPeekVisible, setIsBallPeekVisible] = useState(false)
  const [isBallHovered, setIsBallHovered] = useState(false)
  // 悬浮球组件的位置
  const [position, setPosition] = useState<Position | null>(null)
  // 是否弹出设置弹窗
  const [isShowSetting, setIsShowSetting] = useState(false)
  // 设置弹窗位置
  const [settingPosition, setSettingPosition] = useState<Position | null>(null)
  const isChromiumBlinkVal = useAppSelector(selectIsChromiumBlinkBrowser)
  const connStats = useAppSelector(selectConnStats)
  const isFullScreen = useAppSelector(selectIsFullScreen)
  const isMac = getOS() === OS.MacOS
  const shouldUseMacFullScreenVirtualMouse =
    isFullScreen && isMac && mytest && isChromiumBlinkVal

  // 开始拖拽的信息
  const ballDragState = useRef<DragState>(null)
  const settingDragState = useRef<SettingDragState>(null)
  const ballRef = useRef<HTMLDivElement>(null)
  const positionRef = useRef<Position | null>(null)
  const settingOffsetRef = useRef<Position>({
    x: size + settingGap,
    y: 0,
  })

  const settingWindowRef = useRef<HTMLDivElement>(null)
  // 弹窗尺寸
  const settingViewSize = useRef({ height: 440, width: 315 })
  const shouldSuppressSettingClick = useRef(false)

  const shouldIgnoreDocumentDragEvent = (event: MouseEvent): boolean => {
    return shouldUseMacFullScreenVirtualMouse && event.isTrusted
  }

  const clampBallDragPosition = (nextPosition: Position): Position => {
    const maxX = Math.max(0, window.innerWidth - size)
    const maxY = Math.max(0, window.innerHeight - size)
    return {
      x: clamp(nextPosition.x, 0, maxX),
      y: clamp(nextPosition.y, 0, maxY),
    }
  }

  const clampBallRestPosition = (nextPosition: Position): Position => {
    const maxX = Math.max(0, window.innerWidth - size)
    const maxY = Math.max(0, window.innerHeight - size)

    if (nextPosition.x <= 0) {
      return {
        x: -size + visibleSize,
        y: clamp(nextPosition.y, 0, maxY),
      }
    }

    if (nextPosition.x >= maxX) {
      return {
        x: window.innerWidth - visibleSize,
        y: clamp(nextPosition.y, 0, maxY),
      }
    }

    if (nextPosition.y <= 0) {
      return {
        x: clamp(nextPosition.x, 0, maxX),
        y: -size + visibleSize,
      }
    }

    if (nextPosition.y >= maxY) {
      return {
        x: clamp(nextPosition.x, 0, maxX),
        y: window.innerHeight - visibleSize,
      }
    }

    return clampBallDragPosition(nextPosition)
  }

  const isBallHalfHidden = (nextPosition: Position | null): boolean => {
    if (nextPosition === null) {
      return false
    }

    const maxX = Math.max(0, window.innerWidth - size)
    const maxY = Math.max(0, window.innerHeight - size)
    return (
      nextPosition.x < 0 ||
      nextPosition.x > maxX ||
      nextPosition.y < 0 ||
      nextPosition.y > maxY
    )
  }

  const getBallVisiblePosition = (nextPosition: Position): Position => {
    return clampBallDragPosition({
      x: nextPosition.x < 0 ? 0 : nextPosition.x,
      y: nextPosition.y < 0 ? 0 : nextPosition.y,
    })
  }

  const clampSettingPosition = (nextPosition: Position): Position => {
    const maxX = Math.max(
      windowPadding,
      window.innerWidth - settingViewSize.current.width - windowPadding
    )
    const maxY = Math.max(
      windowPadding,
      window.innerHeight - settingViewSize.current.height - windowPadding
    )
    return {
      x: clamp(nextPosition.x, windowPadding, maxX),
      y: clamp(nextPosition.y, windowPadding, maxY),
    }
  }

  const syncBallPosition = (
    nextPosition: Position,
    persist = false,
    mode: BallPositionMode = "rest"
  ) => {
    const normalizedPosition =
      mode === "drag"
        ? clampBallDragPosition(nextPosition)
        : clampBallRestPosition(nextPosition)
    const displayPosition =
      isBallPeekVisible && isBallHalfHidden(normalizedPosition)
        ? getBallVisiblePosition(normalizedPosition)
        : normalizedPosition

    positionRef.current = normalizedPosition
    setPosition(displayPosition)
    if (persist) {
      SettingEntryPosCache.savePosition(normalizedPosition)
    }
  }

  const syncSettingPosition = (nextPosition: Position) => {
    setSettingPosition(clampSettingPosition(nextPosition))
  }

  const syncSettingOffset = (
    ballPosition: Position,
    nextSettingPosition: Position
  ) => {
    settingOffsetRef.current = {
      x: nextSettingPosition.x - ballPosition.x,
      y: nextSettingPosition.y - ballPosition.y,
    }
  }

  const getDefaultSettingPosition = (
    ballPosition: Position | null
  ): Position | null => {
    if (ballPosition === null) {
      return null
    }

    let x = ballPosition.x + size + settingGap
    if (x + settingViewSize.current.width > window.innerWidth - windowPadding) {
      x = ballPosition.x - settingViewSize.current.width - settingGap
    }
    if (x < windowPadding) {
      x = ballPosition.x
    }

    return clampSettingPosition({
      x,
      y: ballPosition.y,
    })
  }

  const syncSettingWithBall = (
    ballPosition: Position,
    useStoredOffset: boolean
  ) => {
    const nextSettingPosition = useStoredOffset
      ? clampSettingPosition({
          x: ballPosition.x + settingOffsetRef.current.x,
          y: ballPosition.y + settingOffsetRef.current.y,
        })
      : getDefaultSettingPosition(ballPosition)

    if (nextSettingPosition === null) {
      return
    }

    syncSettingPosition(nextSettingPosition)
    syncSettingOffset(ballPosition, nextSettingPosition)
  }

  const syncBallWithSetting = (
    nextSettingPosition: Position,
    mode: BallPositionMode = "drag"
  ) => {
    syncBallPosition(
      {
        x: nextSettingPosition.x - settingOffsetRef.current.x,
        y: nextSettingPosition.y - settingOffsetRef.current.y,
      },
      true,
      mode
    )
    if (positionRef.current !== null) {
      syncSettingOffset(positionRef.current, nextSettingPosition)
    }
  }

  // 处理窗口大小变化
  useEffect(() => {
    const cachedPosition = SettingEntryPosCache.getPosition()
    const defaultPosition = {
      x: window.innerWidth - size,
      y: window.innerHeight / 2 - size / 2,
    }
    syncBallPosition(
      cachedPosition ?? defaultPosition,
      cachedPosition !== null,
      "rest"
    )
  }, [])

  useEffect(() => {
    const onWindowResize = () => {
      if (positionRef.current !== null) {
        syncBallPosition(positionRef.current, true, "rest")
        if (isShowSetting) {
          syncSettingWithBall(positionRef.current, true)
        } else if (settingPosition !== null) {
          syncSettingWithBall(positionRef.current, true)
        }
      }
    }

    if (!isDraggingBall && !isDraggingSetting) {
      window.addEventListener("resize", onWindowResize)
    }

    return () => {
      if (!isDraggingBall && !isDraggingSetting) {
        window.removeEventListener("resize", onWindowResize)
      }
    }
  }, [isDraggingBall, isDraggingSetting, isShowSetting, settingPosition])

  useEffect(() => {
    if (!isChildModalOpen) return
    ballDragState.current = null
    settingDragState.current = null
    setIsDraggingBall(false)
    setIsDraggingSetting(false)
  }, [isChildModalOpen])

  useEffect(() => {
    if (!isDraggingBall) {
      return
    }

    const updateBallPosition = (clientX: number, clientY: number) => {
      const dragState = ballDragState.current
      if (dragState === null) {
        return
      }

      if (
        !dragState.moved &&
        (Math.abs(clientX - dragState.startX) > dragMoveThreshold ||
          Math.abs(clientY - dragState.startY) > dragMoveThreshold)
      ) {
        dragState.moved = true
      }

      if (!dragState.moved) {
        return
      }

      syncBallPosition(
        {
          x: clientX - dragState.offsetX,
          y: clientY - dragState.offsetY,
        },
        false,
        "drag"
      )
      if (positionRef.current !== null) {
        syncSettingWithBall(positionRef.current, settingPosition !== null)
      }
    }

    const finishBallDrag = () => {
      const dragState = ballDragState.current
      setIsDraggingBall(false)
      ballDragState.current = null
      if (dragState === null) {
        return
      }
      if (dragState.moved) {
        if (positionRef.current !== null) {
          syncBallPosition(positionRef.current, true, "rest")
        }
        return
      }
      onClick()
    }

    if (shouldUseMacFullScreenVirtualMouse) {
      const onVirtualMouseMove = (event: Event) => {
        const customEvent = event as CustomEvent<VirtualMousePointerEventDetail>
        updateBallPosition(customEvent.detail.x, customEvent.detail.y)
      }

      const onVirtualMouseUp = () => {
        finishBallDrag()
      }

      document.addEventListener(VIRTUAL_MOUSE_MOVE_EVENT, onVirtualMouseMove)
      document.addEventListener(VIRTUAL_MOUSE_UP_EVENT, onVirtualMouseUp)

      return () => {
        document.removeEventListener(
          VIRTUAL_MOUSE_MOVE_EVENT,
          onVirtualMouseMove
        )
        document.removeEventListener(VIRTUAL_MOUSE_UP_EVENT, onVirtualMouseUp)
      }
    }

    const onMouseMove = (event: MouseEvent) => {
      if (shouldIgnoreDocumentDragEvent(event)) {
        return
      }

      updateBallPosition(event.clientX, event.clientY)
    }

    const onMouseUp = (event: MouseEvent) => {
      if (shouldIgnoreDocumentDragEvent(event)) {
        return
      }

      finishBallDrag()
    }

    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("mouseup", onMouseUp)

    return () => {
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
    }
  }, [isDraggingBall, settingPosition, shouldUseMacFullScreenVirtualMouse])

  useEffect(() => {
    if (!isDraggingSetting) {
      return
    }

    const updateSettingPosition = (clientX: number, clientY: number) => {
      const dragOffset = settingDragState.current
      if (dragOffset === null) {
        return
      }

      if (
        !dragOffset.moved &&
        (Math.abs(clientX - dragOffset.startX) > dragMoveThreshold ||
          Math.abs(clientY - dragOffset.startY) > dragMoveThreshold)
      ) {
        dragOffset.moved = true
      }

      if (!dragOffset.moved) {
        return
      }

      const nextSettingPosition = clampSettingPosition({
        x: clientX - dragOffset.offsetX,
        y: clientY - dragOffset.offsetY,
      })
      syncSettingPosition(nextSettingPosition)
      syncBallWithSetting(nextSettingPosition, "drag")
    }

    const finishSettingDrag = () => {
      const dragOffset = settingDragState.current
      setIsDraggingSetting(false)
      settingDragState.current = null
      if (dragOffset?.moved) {
        shouldSuppressSettingClick.current = true
      }
    }

    if (shouldUseMacFullScreenVirtualMouse) {
      const onVirtualMouseMove = (event: Event) => {
        const customEvent = event as CustomEvent<VirtualMousePointerEventDetail>
        updateSettingPosition(customEvent.detail.x, customEvent.detail.y)
      }

      const onVirtualMouseUp = () => {
        finishSettingDrag()
      }

      document.addEventListener(VIRTUAL_MOUSE_MOVE_EVENT, onVirtualMouseMove)
      document.addEventListener(VIRTUAL_MOUSE_UP_EVENT, onVirtualMouseUp)

      return () => {
        document.removeEventListener(
          VIRTUAL_MOUSE_MOVE_EVENT,
          onVirtualMouseMove
        )
        document.removeEventListener(VIRTUAL_MOUSE_UP_EVENT, onVirtualMouseUp)
      }
    }

    const onMouseMove = (event: MouseEvent) => {
      if (shouldIgnoreDocumentDragEvent(event)) {
        return
      }

      updateSettingPosition(event.clientX, event.clientY)
    }

    const onMouseUp = (event: MouseEvent) => {
      if (shouldIgnoreDocumentDragEvent(event)) {
        return
      }

      finishSettingDrag()
    }

    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("mouseup", onMouseUp)

    return () => {
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
    }
  }, [isDraggingSetting, shouldUseMacFullScreenVirtualMouse])

  // 点击悬浮球
  const onClick = () => {
    if (positionRef.current !== null) {
      syncSettingWithBall(positionRef.current, settingPosition !== null)
    }
    // 显示设置弹窗
    setIsShowSetting(true)
  }

  const onBallMouseDown = (event: React.MouseEvent) => {
    if (isChildModalOpen || position === null || event.button !== 0) {
      return
    }

    event.preventDefault()
    setIsBallPeekVisible(false)
    ballDragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - position.x,
      offsetY: event.clientY - position.y,
      moved: false,
    }
    setIsDraggingBall(true)
  }

  const onBallMouseEnter = () => {
    setIsBallHovered(true)
    if (
      positionRef.current === null ||
      !isBallHalfHidden(positionRef.current)
    ) {
      return
    }

    setIsBallPeekVisible(true)
    setPosition(getBallVisiblePosition(positionRef.current))
  }

  const onBallMouseLeave = () => {
    setIsBallHovered(false)
    if (isDraggingBall || positionRef.current === null) {
      return
    }

    setIsBallPeekVisible(false)
    setPosition(positionRef.current)
  }

  const onBallMouseOver = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return
    }

    onBallMouseEnter()
  }

  const onBallMouseOut = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return
    }

    onBallMouseLeave()
  }

  const onSettingMouseDown = (event: React.MouseEvent) => {
    if (
      isChildModalOpen ||
      event.button !== 0 ||
      settingWindowRef.current === null
    ) {
      return
    }

    const rect = settingWindowRef.current.getBoundingClientRect()
    settingViewSize.current = {
      width: rect.width,
      height: rect.height,
    }
    settingDragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      moved: false,
    }
    setIsDraggingSetting(true)
  }

  const onSettingClickCapture = (event: React.MouseEvent) => {
    if (!shouldSuppressSettingClick.current) {
      return
    }

    shouldSuppressSettingClick.current = false
    event.preventDefault()
    event.stopPropagation()
  }

  useEffect(() => {
    if (
      !shouldUseMacFullScreenVirtualMouse ||
      isShowSetting ||
      ballRef.current === null
    ) {
      return
    }

    const node = ballRef.current
    const syncPeekState = () => {
      if (positionRef.current === null) {
        return
      }

      const isVirtualHover = node.classList.contains("virtual-hover")
      if (isVirtualHover && isBallHalfHidden(positionRef.current)) {
        setIsBallPeekVisible(true)
        setPosition(getBallVisiblePosition(positionRef.current))
        return
      }

      setIsBallPeekVisible(false)
      setPosition(positionRef.current)
    }

    syncPeekState()
    const observer = new MutationObserver(syncPeekState)
    observer.observe(node, {
      attributes: true,
      attributeFilter: ["class"],
    })

    return () => {
      observer.disconnect()
    }
  }, [shouldUseMacFullScreenVirtualMouse, isShowSetting])

  useEffect(() => {
    if (!isShowSetting || settingWindowRef.current === null) {
      return
    }

    const { clientWidth, clientHeight } = settingWindowRef.current
    settingViewSize.current = {
      width: clientWidth,
      height: clientHeight,
    }

    setSettingPosition((currentPosition) => {
      if (positionRef.current === null) {
        return currentPosition
      }
      const useStoredOffset = currentPosition !== null
      const nextPosition = useStoredOffset
        ? clampSettingPosition({
            x: positionRef.current.x + settingOffsetRef.current.x,
            y: positionRef.current.y + settingOffsetRef.current.y,
          })
        : getDefaultSettingPosition(positionRef.current)

      if (nextPosition === null) {
        return currentPosition
      }

      syncSettingOffset(positionRef.current, nextPosition)
      return nextPosition
    })
  }, [isShowSetting])

  if (position === null) {
    return <></>
  }

  const tabs: SettingTab[] = [
    { label: "性能", icon: icVideoStatistic, child: <StatisticContent /> },
    { label: "传输", icon: icFile, child: <FileContent /> },
    {
      label: "设置",
      icon: icSetting,
      child: <SettingsContent onModalOpenChange={setIsChildModalOpen} />,
    },
    {
      label: "隐私",
      icon: icPrivacy,
      child: <PrivacyContent onModalOpenChange={setIsChildModalOpen} />,
    },
  ]

  if (isShowSetting) {
    const style: CSSProperties = {
      position: "fixed",
      left: settingPosition?.x ?? windowPadding,
      top: settingPosition?.y ?? windowPadding,
      zIndex: 999,
    }

    return (
      <div
        ref={settingWindowRef}
        style={style}
        id="full-screen-unvideo-element-tag"
        onMouseDown={onSettingMouseDown}
        onClickCapture={onSettingClickCapture}
        onDragStartCapture={(event) => event.preventDefault()}
      >
        <SettingTabBarView
          tabs={tabs}
          extraActions={[
            // 退出云桌面按钮
            <DisconnectButton key={"action-1"} />,
            // 全屏按钮
            <FullScreenButton key={"action-2"} />,
          ]}
          onClose={() => {
            setIsShowSetting(false)
          }}
        />
      </div>
    )
  }

  const flotBallStyle: CSSProperties = {
    position: "fixed",
    left: position.x,
    top: position.y,
    width: size,
    height: size,
    cursor: isDraggingBall ? "grabbing" : "grab",
    zIndex: 999,
    userSelect: "none",
    opacity: isDraggingBall || isBallHovered || isBallPeekVisible ? 1 : 0.5,
  }
  const ballColor = networkQualityColors[connStats.networkQuality.status]
  const ballShadow = networkQualityShadows[connStats.networkQuality.status]
  const delayText = `${Math.max(0, Math.trunc(connStats.rtt))}`

  return (
    <div
      ref={ballRef}
      id="full-screen-unvideo-element-tag"
      style={flotBallStyle}
      onMouseDown={onBallMouseDown}
      onMouseEnter={onBallMouseEnter}
      onMouseLeave={onBallMouseLeave}
      onMouseOver={onBallMouseOver}
      onMouseOut={onBallMouseOut}
    >
      <div
        style={{
          width: size,
          height: size,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "4px",
          border: `2px solid ${ballColor}`,
          borderRadius: "50%",
          background: "transparent",
          boxShadow: `0 0 4px 6px ${ballShadow}`,
          cursor: isDraggingBall ? "grabbing" : "grab",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            background: "#000",
            color: ballColor,
            fontFamily: "Arial, 'Microsoft YaHei', sans-serif",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <span
            style={{
              fontSize: delayText.length < 3 ? "14px" : "11px",
              fontWeight: 700,
            }}
          >
            {delayText}
          </span>
          <span style={{ marginLeft: "1px", fontSize: "8px", fontWeight: 600 }}>
            ms
          </span>
        </div>
      </div>
    </div>
  )
}

const DisconnectButton: React.FC = () => {
  const dispatch = useAppDispatch()

  return (
    <AppIconTextButton
      src={icExit}
      size={"20px"}
      padding="3px"
      fontSize="11px"
      textString={"断开"}
      onClick={() => {
        dispatch(setIsOpenExitModal(true))
      }}
    />
  )
}

export const ExitRemoteDeskModal: React.FC = () => {
  const isOpen = useAppSelector(selectIsOpenExitModal)
  const dispatch = useAppDispatch()

  useEffect(() => {
    // 退出远程桌面时关闭弹窗
    ConnectionUseCase.addConnCallback({
      onDisconnect: () => {
        dispatch(setIsOpenExitModal(false))
      },
    })
  }, [])

  const exit = () => {
    void exitDocumentFullScreen(() => {
      dispatch(setIsOpenExitModal(false))
    })
    // 退出云桌面，清空资源
    ConnectionUseCase.exitRemoteDesk()
  }

  return (
    <AppDialog
      title="是否确认断开远程桌面？"
      isOpen={isOpen}
      onConfirm={exit}
      onCancel={() => {
        dispatch(setIsOpenExitModal(false))
      }}
    />
  )
}

interface SettingTab {
  label: string
  icon: string
  child: React.ReactNode
}

interface SettingTabBarViewProps {
  tabs: SettingTab[]
  extraActions: React.ReactNode[]
  onClose: () => void
}

const SettingTabBarView: React.FC<SettingTabBarViewProps> = (props) => {
  const { tabs, extraActions, onClose } = props
  // 当前选中的item
  const [selected, setSelected] = useState(0)

  const buildTabs = (): React.ReactNode[] => {
    const unselectedStyle: CSSProperties = {
      width: "100%",
      height: "60px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
      color: "#FFFFFF",
      fontSize: "11px",
      marginBottom: "8px",
    }
    const selectedStyle = {
      ...unselectedStyle,
      background: "#5A5A5A",
      borderTopLeftRadius: "10px",
      borderBottomLeftRadius: "10px",
    }
    return tabs.map((item, index) => {
      const style = selected === index ? selectedStyle : unselectedStyle
      return (
        <Flex
          direction="column"
          key={`tab-${index}`}
          style={style}
          onClick={() => setSelected(index)}
        >
          <img
            src={item.icon}
            style={{
              width: "22px",
              height: "22px",
            }}
          />
          <Box>{item.label}</Box>
        </Flex>
      )
    })
  }

  const buildActions = (): React.ReactNode[] => {
    const children: React.ReactNode[] = []
    extraActions.forEach((item, index) => {
      children.push(item)
      if (index !== extraActions.length - 1) {
        children.push(
          <div
            key={`action-divider-${index}`}
            style={{
              width: "40px",
              height: "1px",
              background: "#383A3E",
              marginTop: "8px",
              marginBottom: "8px",
            }}
          />
        )
      }
    })
    return children
  }

  const buildTitle = (): React.ReactNode => {
    const tab = tabs[selected]
    return (
      <Flex justify="space-between" align="center" style={{ height: "50px" }}>
        {/* 标题 */}
        <div style={{ flex: 1, textAlign: "center" }}>
          <p style={{ color: "white" }}>{tab.label}</p>
        </div>
        {/* 关闭弹窗按钮 */}
        <div style={{ marginRight: "12px" }}>
          <AppIconButton
            src={icClose}
            size="24px"
            padding="6px"
            onClick={onClose}
          />
        </div>
      </Flex>
    )
  }

  const buildTabBars = (): React.ReactNode => {
    return (
      <Flex
        direction="column"
        width="48px"
        background="#000000e8"
        borderTopLeftRadius="10px"
        borderBottomLeftRadius="10px"
        alignItems="center"
        color="#FFFFFF"
      >
        {buildTabs()}
        <Box flex="1" minHeight="24px" />
        {buildActions()}
        <Box height="20px" />
      </Flex>
    )
  }

  return (
    <Flex height="440px">
      {/* 左侧 Tab 栏 */}
      {buildTabBars()}
      <Flex
        direction="column"
        style={{
          width: "280px",
          background: "#000000e8",
          borderTopRightRadius: "10px",
          borderBottomRightRadius: "10px",
        }}
      >
        {/* 顶部标题栏 */}
        {buildTitle()}
        <div
          style={{ width: "100%", height: "0.8px", background: "#ffffff52" }}
        />
        {/* 设置内容 */}
        <Flex flex="1" minHeight="0" overflowY="auto" overflowX="hidden">
          {tabs[selected].child}
        </Flex>
      </Flex>
    </Flex>
  )
}

type SettingDragState = {
  startX: number
  startY: number
  offsetX: number
  offsetY: number
  moved: boolean
} | null

type BallPositionMode = "drag" | "rest"

type DragState = {
  startX: number
  startY: number
  offsetX: number
  offsetY: number
  moved: boolean
} | null
